import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { NavbarComponent } from './theme/navbar/navbar.component';
import { HeaderNavService } from '@layout/services/theme/header-nav.service';
import { Capacitor } from '@capacitor/core';
import { SafeArea } from 'capacitor-plugin-safe-area';
import { Subject, take, takeUntil, tap } from 'rxjs';
import { LayoutWatcherService } from '@layout/services/layout-watcher.service';
import { DEFAULT_HEADER_NAV_HEIGHT } from '@layout/consts/layout-globals.const';
import { GlobalAuthService } from './core/auth/services/global-auth.service';
import { RolesService } from './core/auth/services/roles.service';
import { RolesEnum } from './core/auth/enums/roles-enum';
import { ToastNotificationComponent } from '@shared/components/toast-notification/toast-notification.component';
import { FooterComponent } from './theme/footer/footer.component';

@Component({
  selector: 'mh-root',
  imports: [RouterOutlet, NavbarComponent, ToastNotificationComponent, FooterComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit, OnDestroy {
  // injectables
  private headerNavService = inject(HeaderNavService);
  private layoutWatcherService = inject(LayoutWatcherService);
  private globalAuthService = inject(GlobalAuthService);
  private rolesService = inject(RolesService);
  private cdr = inject(ChangeDetectorRef);

  protected headerConfig = this.headerNavService.configs;
  /** in px */
  protected iosInsetPadding = 0;
  protected contentOffset = signal<number>(DEFAULT_HEADER_NAV_HEIGHT);

  protected isAuthenticated = computed(() => this.globalAuthService.isAuthenticated());

  private _unsubscribe = new Subject<void>();

  constructor() {
    this.initAuth();
    this.headerNavService.toggleNavs(false, 'menu');
    this.headerNavService.toggleNavs(false, 'actions');
  }

  ngOnInit(): void {
    if (!this.isAuthenticated()) {
      return;
    }

    this.setIosSafeArea();

    this.layoutWatcherService
      .watchWindowResize()
      .pipe(
        tap(() => {
          const height = document.getElementById('header-nav')?.clientHeight || DEFAULT_HEADER_NAV_HEIGHT;
          this.contentOffset.set(height);
        }),
        takeUntil(this._unsubscribe),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }

  private initAuth(): void {
    this.globalAuthService.watchAuthEvent().pipe(takeUntil(this._unsubscribe)).subscribe();

    this.globalAuthService
      .getIsAuthenticated()
      .pipe(
        take(1),
        tap((isAuthenticated) => {
          this.headerNavService.toggleNavs(
            !isAuthenticated ||
              (!this.rolesService.hasRoles([RolesEnum.MyHomeUser]) && !this.globalAuthService.selectedSite()),
            'menu',
          );

          setTimeout(() => {
            const height = document.getElementById('header-nav')?.clientHeight || DEFAULT_HEADER_NAV_HEIGHT;
            this.contentOffset.set(height);
          }, 0);
        }),
        takeUntil(this._unsubscribe),
      )
      .subscribe();
  }

  private async setIosSafeArea(): Promise<void> {
    if (Capacitor.getPlatform() == 'ios') {
      SafeArea.getSafeAreaInsets().then(({ insets }) => {
        this.iosInsetPadding = insets.top;
        this.cdr.markForCheck();
      });
    }
  }
}
